import {
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
	type ReactNode,
	type RefObject,
} from "react";
import { createPortal } from "react-dom";

const ANCHOR_GAP = 4;
const VIEWPORT_MARGIN = 8;
const ANCHORED_Z_INDEX = 100;

interface PopoverProps {
	/** 開閉ボタン。ここへのクリックは外側クリックとして扱わない (ボタン側がトグルする)。 */
	anchorRef: RefObject<HTMLElement | null>;
	onClose: () => void;
	/**
	 * true ならアンカーの下 (入りきらなければ上) に align の端を揃えて固定配置し、はみ出す分は画面内へ寄せる。
	 * false なら呼び出し側が配置する。
	 */
	anchored?: boolean;
	/** anchored のとき、アンカーとどちらの端を揃えるか。"right" なら右端を揃えて左へ広げる。 */
	align?: "left" | "right";
	/** false の間は外側クリック・Escape で閉じない (上に別の UI を重ねている間など)。 */
	dismissible?: boolean;
	/** anchored のとき、背後のページを薄く暗くしてポップオーバーを見分けやすくする。 */
	dimBackground?: boolean;
	/** anchored のとき、配置が決まって表示された時点でフォーカスする要素。 */
	initialFocusRef?: RefObject<HTMLElement | null>;
	width: number;
	padding?: number;
	testId?: string;
	children: ReactNode;
}

/** アンカーに紐づく一時的なパネル。外側クリックか Escape で閉じる。 */
export function Popover({
	anchorRef,
	onClose,
	anchored = false,
	align = "left",
	dismissible = true,
	dimBackground = false,
	initialFocusRef,
	width,
	padding = 0,
	testId,
	children,
}: PopoverProps) {
	const containerRef = useRef<HTMLDivElement>(null);
	const [placement, setPlacement] = useState<{
		top: number;
		left: number;
		above: boolean;
	} | null>(null);

	useLayoutEffect(() => {
		if (!anchored) return;
		const anchor = anchorRef.current;
		const container = containerRef.current;
		if (!anchor || !container) return;
		const anchorRect = anchor.getBoundingClientRect();
		// 出現アニメの scale を含まない寸法で測る。
		const height = container.offsetHeight;
		const below = anchorRect.bottom + ANCHOR_GAP;
		const aboveTop = anchorRect.top - ANCHOR_GAP - height;
		const fitsBelow = below + height <= window.innerHeight - VIEWPORT_MARGIN;
		const above = !fitsBelow && aboveTop >= VIEWPORT_MARGIN;
		const top = fitsBelow ? below : Math.max(VIEWPORT_MARGIN, aboveTop);
		const left = Math.max(
			VIEWPORT_MARGIN,
			Math.min(
				align === "right" ? anchorRect.right - width : anchorRect.left,
				window.innerWidth - VIEWPORT_MARGIN - width,
			),
		);
		setPlacement({ top, left, above });
	}, [anchored, align, anchorRef, width]);

	// 配置が決まるまでは visibility: hidden でフォーカスできないため、表示後に当てる。
	const placed = placement !== null;
	useEffect(() => {
		if (placed) initialFocusRef?.current?.focus();
	}, [placed, initialFocusRef]);

	useEffect(() => {
		if (!dismissible) return;
		const onPointerDown = (e: PointerEvent) => {
			const target = e.target as Node;
			if (containerRef.current?.contains(target) || anchorRef.current?.contains(target)) return;
			onClose();
		};
		const onKeyDown = (e: KeyboardEvent) => {
			if (e.key !== "Escape" || e.defaultPrevented) return;
			// 中身にフォーカスがあったまま閉じると body へ落ちるので、開閉ボタンへ戻す。
			if (containerRef.current?.contains(document.activeElement)) anchorRef.current?.focus();
			onClose();
		};
		document.addEventListener("pointerdown", onPointerDown);
		document.addEventListener("keydown", onKeyDown);
		return () => {
			document.removeEventListener("pointerdown", onPointerDown);
			document.removeEventListener("keydown", onKeyDown);
		};
	}, [anchorRef, dismissible, onClose]);

	const panel = (
		<div
			ref={containerRef}
			data-testid={testId}
			className="izanagi-dropdown"
			data-placement={placement?.above ? "above" : undefined}
			style={{
				width,
				padding,
				background: "var(--surface-1)",
				borderRadius: "var(--radius-6)",
				boxShadow: "var(--shadow-menu)",
				overflow: "hidden",
				...(anchored
					? {
							position: "fixed",
							top: placement?.top ?? 0,
							left: placement?.left ?? 0,
							visibility: placement ? "visible" : "hidden",
							zIndex: ANCHORED_Z_INDEX,
						}
					: {}),
			}}
		>
			{children}
		</div>
	);
	if (!anchored) return panel;
	return createPortal(
		<>
			{dimBackground && (
				<div
					style={{
						position: "fixed",
						inset: 0,
						zIndex: ANCHORED_Z_INDEX - 1,
						background: "var(--scrim-light)",
					}}
				/>
			)}
			{panel}
		</>,
		document.body,
	);
}
